import { request } from './client'
import type {
  AccessReason,
  AdminAccess,
  ProvisioningAttribute,
  ProvisioningReport,
  ProvisioningRule,
  ProvisioningRun,
  AdminBot,
  AdminBotWithKey,
  AdminDeviceDetail,
  AdminDevicesPage,
  AdminDevicesParams,
  AdminUser,
  AdminUsersParams,
  AttestBody,
  AttachmentUrl,
  AuditEntry,
  Bot,
  BulkInstallResult,
  ChannelDetail,
  ChannelEvent,
  ChannelSummary,
  CreateBotBody,
  CreateChannelBody,
  CreatedBot,
  CreateIntegrationBody,
  DeviceChallenge,
  DeviceInfo,
  DeviceSession,
  ForwardRequest,
  ForwardResponse,
  InstalledChannel,
  Integration,
  IntegrationWithKey,
  ManageChannelBody,
  Me,
  MeSettings,
  MemberRole,
  MessagesPage,
  MyDevice,
  Notify,
  PatchBotBody,
  PatchChannelBody,
  PatchIntegrationBody,
  PresignRequest,
  PresignResponse,
  PushSubscriptionBody,
  Role,
  SearchParams,
  SearchResponse,
  SendMessageBody,
  SetBotWebhookResult,
  SyncRequest,
  SyncResponse,
  User,
  WebhookDeliveriesPage,
  WebhookDelivery,
  WebhookReceives,
} from './types'

const enc = encodeURIComponent

/** Sysadmin override of the managed-channel rules (audited server-side). */
export interface OverrideOptions {
  override?: boolean
}

const overrideQuery = (options?: OverrideOptions) => (options?.override ? { override: true } : undefined)

export const api = {
  // Auth
  logout: (app?: 'ios' | 'android') => request<{ redirectUrl: string }>('/auth/logout', { method: 'POST', query: app ? { app } : undefined }),

  // Me and sessions
  me: () => request<Me>('/api/me', { onUnauthorized: 'gate' }),
  updateSettings: (settings: MeSettings) => request<Me>('/api/me/settings', { method: 'PATCH', body: settings }),
  sessions: () => request<DeviceSession[]>('/api/me/sessions'),
  revokeSession: (id: string) => request<void>(`/api/me/sessions/${enc(id)}`, { method: 'DELETE' }),
  revokeAllSessions: () => request<void>('/api/me/sessions', { method: 'DELETE' }),
  putNativePush: (body: { platform: 'ios' | 'android'; token: string }) => request<{ id: string }>('/api/me/push-native', { method: 'PUT', body }),
  deleteNativePush: (token: string) => request<void>('/api/me/push-native', { method: 'DELETE', query: { token } }),
  putPushSubscription: (body: PushSubscriptionBody) =>
    request<{ id: string }>('/api/me/push-subscriptions', { method: 'PUT', body }),
  deletePushSubscription: (endpoint: string) =>
    request<void>('/api/me/push-subscriptions', { method: 'DELETE', query: { endpoint } }),

  // Devices
  deviceChallenge: () => request<DeviceChallenge>('/api/devices/challenge', { method: 'POST' }),
  attestDevice: (body: AttestBody) => request<DeviceInfo>('/api/devices/attest', { method: 'POST', body }),
  /** Polled from the gate; a 401 there means the session ended, so show the sign-in screen. */
  deviceMe: () => request<DeviceInfo>('/api/devices/me', { onUnauthorized: 'gate' }),
  myDevices: () => request<MyDevice[]>('/api/me/devices'),
  renameMyDevice: (id: string, name: string) =>
    request<MyDevice>(`/api/me/devices/${enc(id)}`, { method: 'PATCH', body: { name } }),
  removeMyDevice: (id: string) => request<void>(`/api/me/devices/${enc(id)}`, { method: 'DELETE' }),

  // Sync
  sync: (body: SyncRequest) => request<SyncResponse>('/api/sync', { method: 'POST', body }),

  // Channels
  channels: (scope: 'mine' | 'all') => request<ChannelSummary[]>('/api/channels', { query: { scope } }),
  createChannel: (body: CreateChannelBody) => request<ChannelSummary>('/api/channels', { method: 'POST', body }),
  channel: (id: string) => request<ChannelDetail>(`/api/channels/${enc(id)}`),
  /** Says why a conversation this administrator is not in is being opened; the answer holds for a few hours. */
  openAdminAccess: (id: string, reason: AccessReason, note: string) =>
    request<AdminAccess>(`/api/channels/${enc(id)}/access`, { method: 'POST', body: { reason, note } }),
  patchChannel: (id: string, body: PatchChannelBody, options?: OverrideOptions) =>
    request<ChannelSummary>(`/api/channels/${enc(id)}`, { method: 'PATCH', body, query: overrideQuery(options) }),
  addMembers: (id: string, userIds: string[]) =>
    request<void>(`/api/channels/${enc(id)}/members`, { method: 'POST', body: { userIds } }),
  removeMember: (id: string, userId: string, options?: OverrideOptions) =>
    request<void>(`/api/channels/${enc(id)}/members/${enc(userId)}`, { method: 'DELETE', query: overrideQuery(options) }),
  setMemberRole: (id: string, userId: string, role: MemberRole, options?: OverrideOptions) =>
    request<void>(`/api/channels/${enc(id)}/members/${enc(userId)}`, { method: 'PATCH', body: { role }, query: overrideQuery(options) }),
  join: (id: string) => request<void>(`/api/channels/${enc(id)}/join`, { method: 'POST' }),
  setNotify: (id: string, notify: Notify, mutedUntil: string | null) =>
    request<void>(`/api/channels/${enc(id)}/notify`, { method: 'PUT', body: { notify, mutedUntil } }),
  markRead: (id: string, seq: number) =>
    request<void>(`/api/channels/${enc(id)}/read`, { method: 'POST', body: { seq } }),
  messages: (id: string, beforeSeq: number, limit = 50) =>
    request<MessagesPage>(`/api/channels/${enc(id)}/messages`, { query: { beforeSeq, limit } }),
  // A group chat is named when it is started; a direct or bot conversation is named after who is in it.
  createDm: (userIds: string[], name?: string) => request<ChannelSummary>('/api/dms', { method: 'POST', body: name ? { userIds, name } : { userIds } }),
  /** The caller's bot conversation with a restricted channel's reply bot (409 replies_off when replies are off or paused). */
  replyConversation: (channelId: string) =>
    request<ChannelSummary>(`/api/channels/${enc(channelId)}/reply-conversation`, { method: 'POST' }),

  // Bots (contract: "Bots")
  bots: () => request<Bot[]>('/api/bots'),
  /** 204 (undefined) when the bot is already installed. */
  installBot: (channelId: string, integrationId: string) =>
    request<ChannelEvent | undefined>(`/api/channels/${enc(channelId)}/bots`, { method: 'POST', body: { integrationId } }),
  uninstallBot: (channelId: string, integrationId: string) =>
    request<ChannelEvent>(`/api/channels/${enc(channelId)}/bots/${enc(integrationId)}`, { method: 'DELETE' }),

  // Messages
  sendMessage: (channelId: string, body: SendMessageBody) =>
    request<ChannelEvent>(`/api/channels/${enc(channelId)}/messages`, { method: 'POST', body }),
  /** Per-target results; the request fails only for the message itself (403, 404, 409 message_deleted, media_processing, media_failed). */
  forwardMessage: (id: string, body: ForwardRequest) =>
    request<ForwardResponse>(`/api/messages/${enc(id)}/forward`, { method: 'POST', body }),
  editMessage: (id: string, body: string) =>
    request<ChannelEvent>(`/api/messages/${enc(id)}`, { method: 'PATCH', body: { body } }),
  deleteMessage: (id: string) => request<ChannelEvent>(`/api/messages/${enc(id)}`, { method: 'DELETE' }),
  addReaction: (id: string, emoji: string) =>
    request<ChannelEvent | undefined>(`/api/messages/${enc(id)}/reactions/${enc(emoji)}`, { method: 'PUT' }),
  removeReaction: (id: string, emoji: string) =>
    request<ChannelEvent | undefined>(`/api/messages/${enc(id)}/reactions/${enc(emoji)}`, { method: 'DELETE' }),
  tick: (id: string, itemId: string) =>
    request<ChannelEvent | undefined>(`/api/messages/${enc(id)}/ticks/${enc(itemId)}`, { method: 'PUT' }),
  untick: (id: string, itemId: string) =>
    request<ChannelEvent | undefined>(`/api/messages/${enc(id)}/ticks/${enc(itemId)}`, { method: 'DELETE' }),
  pin: (id: string) => request<ChannelEvent | undefined>(`/api/messages/${enc(id)}/pin`, { method: 'PUT' }),
  unpin: (id: string) => request<ChannelEvent | undefined>(`/api/messages/${enc(id)}/pin`, { method: 'DELETE' }),

  // Attachments
  presign: (channelId: string, body: PresignRequest, signal?: AbortSignal) =>
    request<PresignResponse>(`/api/channels/${enc(channelId)}/attachments`, { method: 'POST', body, signal }),
  attachmentUrl: (id: string, variant: 'original' | 'thumb') =>
    request<AttachmentUrl>(`/api/attachments/${enc(id)}/url`, { query: { variant } }),

  // Users, presence, search
  searchUsers: (q: string, limit = 20) => request<User[]>('/api/users', { query: { q, limit } }),
  usersByIds: (ids: string[]) => request<User[]>('/api/users', { query: { ids: ids.join(',') } }),
  presence: () => request<string[]>('/api/presence'),
  search: (params: SearchParams) => request<SearchResponse>('/api/search', { query: { ...params } }),

  // Admin
  adminUsers: (q: string, params: AdminUsersParams = {}) => request<AdminUser[]>('/api/admin/users', { query: { q, ...params } }),
  setUserRole: (id: string, role: Role) =>
    request<void>(`/api/admin/users/${enc(id)}/role`, { method: 'PUT', body: { role } }),
  revokeUserSessions: (id: string) =>
    request<void>(`/api/admin/users/${enc(id)}/revoke-sessions`, { method: 'POST' }),
  audit: (from?: string, to?: string, limit = 100) =>
    request<AuditEntry[]>('/api/admin/audit', { query: { from, to, limit } }),
  adminDevices: (params: AdminDevicesParams = {}) =>
    request<AdminDevicesPage>('/api/admin/devices', { query: { ...params } }),
  adminDevice: (id: string) => request<AdminDeviceDetail>(`/api/admin/devices/${enc(id)}`),
  approveDevice: (id: string, body: { name?: string; note?: string } = {}) =>
    request<void>(`/api/admin/devices/${enc(id)}/approve`, { method: 'POST', body }),
  denyDevice: (id: string, body: { note?: string } = {}) =>
    request<void>(`/api/admin/devices/${enc(id)}/deny`, { method: 'POST', body }),
  revokeDevice: (id: string, body: { note?: string } = {}) =>
    request<void>(`/api/admin/devices/${enc(id)}/revoke`, { method: 'POST', body }),
  patchDevice: (id: string, body: { name?: string; note?: string }) =>
    request<void>(`/api/admin/devices/${enc(id)}`, { method: 'PATCH', body }),
  deleteDevice: (id: string) => request<void>(`/api/admin/devices/${enc(id)}`, { method: 'DELETE' }),

  // Admin: integrations and managed channels (sysadmin)
  integrations: () => request<Integration[]>('/api/admin/integrations'),
  createIntegration: (body: CreateIntegrationBody) =>
    request<IntegrationWithKey>('/api/admin/integrations', { method: 'POST', body }),
  patchIntegration: (id: string, body: PatchIntegrationBody) =>
    request<Integration>(`/api/admin/integrations/${enc(id)}`, { method: 'PATCH', body }),
  /** graceHours 0..24 (server default 1): how long the previous keys keep working. */
  rotateIntegration: (id: string, graceHours?: number) =>
    request<IntegrationWithKey>(`/api/admin/integrations/${enc(id)}/rotate`, { method: 'POST', body: { graceHours } }),
  revokeIntegration: (id: string) => request<void>(`/api/admin/integrations/${enc(id)}/revoke`, { method: 'POST' }),
  /** PNG, JPEG or WebP up to 256 KB; stored as a 128×128 PNG. */
  uploadIntegrationAvatar: (id: string, file: Blob) =>
    request<Integration>(`/api/admin/integrations/${enc(id)}/avatar`, { method: 'PUT', rawBody: file }),
  deleteIntegrationAvatar: (id: string) => request<Integration>(`/api/admin/integrations/${enc(id)}/avatar`, { method: 'DELETE' }),
  integrationChannels: (id: string) => request<InstalledChannel[]>(`/api/admin/integrations/${enc(id)}/channels`),
  installBotInChannels: (id: string, channelIds: string[]) =>
    request<BulkInstallResult>(`/api/admin/integrations/${enc(id)}/channels`, { method: 'POST', body: { channelIds } }),

  // Admin: bots (sysadmin, contract: "Sysadmin: bots")
  provisioningRules: () => request<ProvisioningRule[]>('/api/admin/provisioning/rules'),
  saveProvisioningRules: (rules: ProvisioningRule[]) => request<ProvisioningRule[]>('/api/admin/provisioning/rules', { method: 'PUT', body: { rules } }),
  previewProvisioning: (rules: ProvisioningRule[]) => request<ProvisioningReport>('/api/admin/provisioning/preview', { method: 'POST', body: { rules } }),
  provisioningAttributes: () => request<ProvisioningAttribute[]>('/api/admin/provisioning/attributes'),
  provisioningRuns: () => request<ProvisioningRun[]>('/api/admin/provisioning/runs', { query: { limit: 20 } }),
  runProvisioning: () => request<void>('/api/admin/provisioning/runs', { method: 'POST' }),
  approveProvisioningRun: (id: string) => request<ProvisioningRun>(`/api/admin/provisioning/runs/${enc(id)}/approve`, { method: 'POST' }),
  adminBots: () => request<AdminBot[]>('/api/admin/bots'),
  createBot: (body: CreateBotBody) => request<CreatedBot>('/api/admin/bots', { method: 'POST', body }),
  patchBot: (id: string, body: PatchBotBody) => request<AdminBot>(`/api/admin/bots/${enc(id)}`, { method: 'PATCH', body }),
  uploadBotAvatar: (id: string, file: Blob) => request<AdminBot>(`/api/admin/bots/${enc(id)}/avatar`, { method: 'PUT', rawBody: file }),
  deleteBotAvatar: (id: string) => request<AdminBot>(`/api/admin/bots/${enc(id)}/avatar`, { method: 'DELETE' }),
  setBotWebhook: (id: string, body: { url: string; receives: WebhookReceives }) =>
    request<SetBotWebhookResult>(`/api/admin/bots/${enc(id)}/webhook`, { method: 'PUT', body }),
  removeBotWebhook: (id: string) => request<AdminBot>(`/api/admin/bots/${enc(id)}/webhook`, { method: 'DELETE' }),
  rotateBotWebhookSecret: (id: string) => request<{ webhookSecret: string }>(`/api/admin/bots/${enc(id)}/webhook/rotate-secret`, { method: 'POST' }),
  /** A signed ping, sent immediately. */
  testBotWebhook: (id: string) => request<WebhookDelivery>(`/api/admin/bots/${enc(id)}/webhook/test`, { method: 'POST' }),
  botDeliveries: (id: string, cursor?: string | null) =>
    request<WebhookDeliveriesPage>(`/api/admin/bots/${enc(id)}/deliveries`, { query: { cursor } }),
  /** graceHours 0..24: how long the previous keys keep working. */
  rotateBotKey: (id: string, graceHours?: number) =>
    request<AdminBotWithKey>(`/api/admin/bots/${enc(id)}/rotate-key`, { method: 'POST', body: { graceHours } }),
  revokeBot: (id: string) => request<void>(`/api/admin/bots/${enc(id)}/revoke`, { method: 'POST' }),
  manageChannel: (id: string, body: ManageChannelBody) =>
    request<ChannelSummary>(`/api/admin/channels/${enc(id)}/manage`, { method: 'POST', body }),
  releaseChannel: (id: string) => request<ChannelSummary>(`/api/admin/channels/${enc(id)}/release`, { method: 'POST' }),
}

/** Stable same-origin URL; the server 302s to a short-lived presigned URL. */
export function attachmentContentUrl(id: string, variant: 'original' | 'thumb'): string {
  return `/api/attachments/${enc(id)}/content?variant=${variant}`
}

/** The file served as a download under its own name, whatever its type. */
export function attachmentDownloadUrl(id: string): string {
  return `${attachmentContentUrl(id, 'original')}&download=1`
}

export function exportChannelUrl(id: string): string {
  return `/api/admin/channels/${enc(id)}/export`
}

export function loginUrl(returnTo: string): string {
  return `/auth/login?returnTo=${enc(returnTo)}`
}
